import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { CegidSyncButton } from "../components/CegidSyncButton";
import { AutoReviewTab } from "../components/accounting/AutoReviewTab";
import { BankConnectionsPanel } from "../components/accounting/BankConnectionsPanel";
import { MailboxTab } from "../components/accounting/MailboxTab";
import PageHeader from "../components/PageHeader";
import {
  BookOpen, Calculator, FileText, Banknote, FileBarChart, Download,
  Loader2, Plus, Trash2, CheckCircle2, RotateCcw, Lock, Unlock,
  ListTree, AlertTriangle, Layers, Upload, Sparkles, RefreshCw, Search, X, Cloud, Zap, Mail,
} from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-[#0b0811] border border-white/10 text-white placeholder:text-slate-500 focus:border-[#f7c935]";

const TABS = [
  { key: "auto", label: "Automatisation", icon: Zap },
  { key: "entries", label: "Ecritures", icon: FileText },
  { key: "bank", label: "Banque", icon: Banknote },
  { key: "mailbox", label: "Fournisseurs", icon: Mail },
  { key: "lettrage", label: "Lettrage", icon: Layers },
  { key: "reports", label: "Reports", icon: FileBarChart },
  { key: "consolidated", label: "Consolidation", icon: Sparkles },
  { key: "accounts", label: "Plan comptable", icon: ListTree },
  { key: "journals", label: "Journaux", icon: BookOpen },
  { key: "fiscal", label: "Exercices", icon: Calculator },
];

const STATUS_META = {
  validated: { label: "VALIDEE", color: "#22c55e" },
  to_review: { label: "A VERIFIER", color: "#f97316" },
  draft: { label: "BROUILLARD", color: "#f7c935" },
};

export default function AccountingPage() {
  const { axiosAuth, user } = useAuth();
  const api = axiosAuth();
  const { currentSociete, societes, switchSociete } = useSociete();
  const societeId = currentSociete?.societe_id || societes?.[0]?.societe_id;
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(TABS.some(t => t.key === searchParams.get("tab")) ? searchParams.get("tab") : "auto");
  const [reviewCount, setReviewCount] = useState(null);

  useEffect(() => {
    if (!societeId) return;
    api.get("/accounting/auto/overview", { params: { societe_id: societeId } })
      .then((r) => setReviewCount(r.data.counts.to_review))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societeId, tab]);

  if (!societeId) {
    return (
      <div className="p-8 max-w-4xl mx-auto">
        <div className="bg-amber-500/10 border border-amber-500/20 text-amber-300 rounded-xl p-4 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Aucune societe selectionnee. Selectionnez une societe pour acceder a la comptabilite.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1280px] mx-auto" data-testid="accounting-page">
      <PageHeader
        icon={<Calculator />}
        title="Comptabilité générale"
        description="Plan comptable PCG · Écritures · Lettrage · Rapprochement bancaire · FEC · Bilan / CR"
        testId="accounting-header"
        actions={(
          <>
            {(user?.role === "admin" || user?.role === "superadmin") && <CegidSyncButton api={api} societeId={societeId} />}
            {societes && societes.length > 1 ? (
              <select
                value={currentSociete?.societe_id || societes[0]?.societe_id}
                onChange={(e) => {
                  const s = societes.find(x => x.societe_id === e.target.value);
                  if (s) switchSociete(s);
                }}
                className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm focus:border-[#f7c935] outline-none"
                data-testid="accounting-societe-selector"
              >
                {societes.map(s => (
                  <option key={s.societe_id} value={s.societe_id}>{s.name}</option>
                ))}
              </select>
            ) : (
              <div className="text-xs text-slate-500">Société : <span className="text-white font-bold">{currentSociete?.name || societes?.[0]?.name}</span></div>
            )}
          </>
        )}
      />

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-5 bg-[#0b0811] border border-white/5 rounded-xl p-1" data-testid="accounting-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors ${active ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
              {t.key === "auto" && reviewCount > 0 && (
                <span className={`ml-1 text-[10px] font-black px-1.5 py-0.5 rounded-full ${active ? "bg-[#0b0811] text-[#f7c935]" : "bg-[#f97316] text-[#0b0811]"}`} data-testid="tab-auto-badge">{reviewCount}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
        {tab === "auto" && <AutoReviewTab api={api} societeId={societeId} onCountChange={setReviewCount} />}
        {tab === "accounts" && <AccountsTab api={api} societeId={societeId} />}
        {tab === "journals" && <JournalsTab api={api} societeId={societeId} />}
        {tab === "fiscal" && <FiscalTab api={api} societeId={societeId} />}
        {tab === "entries" && <EntriesTab api={api} societeId={societeId} />}
        {tab === "lettrage" && <LettrageTab api={api} societeId={societeId} />}
        {tab === "bank" && <BankTab api={api} societeId={societeId} />}
        {tab === "mailbox" && <MailboxTab api={api} societeId={societeId} />}
        {tab === "reports" && <ReportsTab api={api} societeId={societeId} />}
        {tab === "consolidated" && <ConsolidatedTab api={api} societes={societes} />}
      </div>
    </div>
  );
}

// ─────────── TAB: Plan comptable ────────────────────────────
function AccountsTab({ api, societeId }) {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [classeFilter, setClasseFilter] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/accounting/accounts", { params: { societe_id: societeId, q: q || undefined, classe: classeFilter || undefined } });
      setAccounts(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [societeId, classeFilter]);

  const seedPcg = async () => {
    if (!window.confirm("Importer le plan comptable francais standard PCG ? Les comptes existants seront conserves.")) return;
    setSeeding(true);
    try {
      const r = await api.post(`/accounting/init/seed-pcg`, null, { params: { societe_id: societeId } });
      toast.success(`PCG importe : ${r.data.inserted_accounts} nouveaux comptes`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSeeding(false);
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    try { await api.delete(`/accounting/accounts/${id}`); toast.success("Supprime"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="accounts-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input placeholder="Rechercher numero ou libelle..." value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()}
            className={`${inputCls} pl-9 h-9`} data-testid="acc-search" />
        </div>
        {[1, 2, 4, 5, 6, 7].map(c => (
          <button key={c} onClick={() => setClasseFilter(classeFilter === c ? null : c)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${classeFilter === c ? "bg-[#f7c935] text-[#0b0811]" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}>
            Classe {c}
          </button>
        ))}
        {(user?.role !== "tenant_member" || !!(user?.module_permissions || {})["comptabilite.edit"]) && (
          <>
            <Button size="sm" onClick={seedPcg} disabled={seeding} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="seed-pcg-btn">
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Importer PCG
            </Button>
            <Button size="sm" onClick={() => setShowNew(true)} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="new-account-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouveau compte
            </Button>
          </>
        )}
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-[#f7c935]" /> : (
        <div className="overflow-x-auto rounded-lg border border-white/5">
          <table className="w-full text-xs">
            <thead className="bg-[#0b0811] text-slate-400">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Numero</th>
                <th className="text-left px-3 py-2 font-bold">Libelle</th>
                <th className="text-left px-3 py-2 font-bold">Classe</th>
                <th className="text-left px-3 py-2 font-bold">Type</th>
                <th className="text-center px-3 py-2 font-bold">Lettrable</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.account_id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`acc-row-${a.account_number}`}>
                  <td className="px-3 py-2 font-mono text-white">{a.account_number}</td>
                  <td className="px-3 py-2 text-slate-300">{a.label}</td>
                  <td className="px-3 py-2 text-slate-500">{a.classe}</td>
                  <td className="px-3 py-2 text-slate-500">{a.type}</td>
                  <td className="px-3 py-2 text-center">{a.lettrable ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 inline" /> : ""}</td>
                  <td className="px-3 py-2 text-right">
                    {!a.system && (
                      <button onClick={() => del(a.account_id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
              {accounts.length === 0 && <tr><td colSpan="6" className="text-center py-10 text-slate-500">Aucun compte. Cliquez sur "Importer PCG" pour seeder le plan standard.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {showNew && <AccountDialog api={api} societeId={societeId} onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function AccountDialog({ api, societeId, onClose }) {
  const [f, setF] = useState({ account_number: "", label: "", classe: 6, type: "charge", lettrable: false, societe_id: societeId });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post("/accounting/accounts", f); toast.success("Compte cree"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white">
        <DialogHeader><DialogTitle>Nouveau compte</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Numero (ex: 622600)" value={f.account_number} onChange={e => setF({ ...f, account_number: e.target.value })} className={inputCls} data-testid="new-acc-number" />
          <Input placeholder="Libelle" value={f.label} onChange={e => setF({ ...f, label: e.target.value })} className={inputCls} data-testid="new-acc-label" />
          <div className="grid grid-cols-2 gap-2">
            <select value={f.classe} onChange={e => setF({ ...f, classe: parseInt(e.target.value) })} className={`${inputCls} h-10 px-3 rounded`}>
              {[1, 2, 3, 4, 5, 6, 7, 8].map(c => <option key={c} value={c}>Classe {c}</option>)}
            </select>
            <select value={f.type} onChange={e => setF({ ...f, type: e.target.value })} className={`${inputCls} h-10 px-3 rounded`}>
              {["actif", "passif", "charge", "produit", "tiers_client", "tiers_fournisseur"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={f.lettrable} onChange={e => setF({ ...f, lettrable: e.target.checked })} /> Compte lettrable
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5">Annuler</Button>
          <Button onClick={save} disabled={saving || !f.account_number || !f.label} className="bg-[#f7c935] text-[#0b0811] font-bold" data-testid="save-acc-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />} Creer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── TAB: Journaux ──────────────────────────────────
function JournalsTab({ api, societeId }) {
  const [journals, setJournals] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = async () => {
    try { const r = await api.get("/accounting/journals", { params: { societe_id: societeId } }); setJournals(r.data); } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [societeId]);
  return (
    <div data-testid="journals-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-500">Journaux configures pour cette societe</span>
        <Button size="sm" onClick={() => setShowNew(true)} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-9"><Plus className="w-3.5 h-3.5 mr-1.5" /> Nouveau journal</Button>
      </div>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {journals.map(j => (
          <div key={j.journal_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-4" data-testid={`journal-${j.code}`}>
            <div className="flex items-center justify-between">
              <span className="text-2xl font-black text-[#f7c935]">{j.code}</span>
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{j.type}</span>
            </div>
            <div className="text-sm font-medium text-white mt-1">{j.label}</div>
            {j.default_account && <div className="text-[10px] text-slate-500 mt-1">Compte par defaut : {j.default_account}</div>}
          </div>
        ))}
      </div>
      {showNew && <JournalDialog api={api} societeId={societeId} onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function JournalDialog({ api, societeId, onClose }) {
  const [f, setF] = useState({ code: "", label: "", type: "od", default_account: "", societe_id: societeId });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post("/accounting/journals", f); toast.success("Journal cree"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white">
        <DialogHeader><DialogTitle>Nouveau journal</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Code (2-6 maj/chiffres ex: BQ2)" value={f.code} onChange={e => setF({ ...f, code: e.target.value.toUpperCase() })} className={inputCls} />
          <Input placeholder="Libelle" value={f.label} onChange={e => setF({ ...f, label: e.target.value })} className={inputCls} />
          <select value={f.type} onChange={e => setF({ ...f, type: e.target.value })} className={`${inputCls} h-10 px-3 rounded`}>
            {["achat", "vente", "banque", "caisse", "od", "an"].map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <Input placeholder="Compte par defaut (optionnel)" value={f.default_account} onChange={e => setF({ ...f, default_account: e.target.value })} className={`${inputCls} font-mono`} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5">Annuler</Button>
          <Button onClick={save} disabled={saving || !f.code || !f.label} className="bg-[#f7c935] text-[#0b0811] font-bold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />} Creer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── TAB: Exercices ─────────────────────────────────
function FiscalTab({ api, societeId }) {
  const [years, setYears] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = async () => {
    try { const r = await api.get("/accounting/fiscal-years", { params: { societe_id: societeId } }); setYears(r.data); } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [societeId]);
  const close = async (id) => {
    if (!window.confirm("Cloturer cet exercice et generer les a-nouveaux dans l'exercice suivant ?")) return;
    try { const r = await api.post(`/accounting/fiscal-years/${id}/close`); toast.success(`Exercice cloture - Resultat: ${r.data.result} EUR`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const reopen = async (id) => {
    if (!window.confirm("Reouvrir cet exercice ?")) return;
    try { await api.post(`/accounting/fiscal-years/${id}/reopen`); toast.success("Exercice reouvert"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <div data-testid="fiscal-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-500">Exercices comptables</span>
        <Button size="sm" onClick={() => setShowNew(true)} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-9" data-testid="new-fy-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvel exercice</Button>
      </div>
      <div className="space-y-2">
        {years.map(y => (
          <div key={y.fiscal_year_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-4 flex items-center justify-between" data-testid={`fy-${y.code}`}>
            <div>
              <div className="font-bold text-white">{y.code}</div>
              <div className="text-xs text-slate-500">{y.start_date} → {y.end_date}</div>
              {y.result !== undefined && <div className="text-xs mt-1" style={{ color: y.result >= 0 ? "#22c55e" : "#ef4444" }}>Resultat : {y.result?.toLocaleString("fr-FR")} EUR</div>}
            </div>
            <div className="flex items-center gap-2">
              {y.status === "open" ? (
                <>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 font-bold">OUVERT</span>
                  <Button size="sm" variant="outline" onClick={() => close(y.fiscal_year_id)} className="border-white/10 text-white hover:bg-white/5 text-xs h-8"><Lock className="w-3 h-3 mr-1" /> Cloturer</Button>
                </>
              ) : (
                <>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-500/10 text-slate-400 font-bold">CLOTURE</span>
                  <Button size="sm" variant="outline" onClick={() => reopen(y.fiscal_year_id)} className="border-white/10 text-white hover:bg-white/5 text-xs h-8"><Unlock className="w-3 h-3 mr-1" /> Rouvrir</Button>
                </>
              )}
            </div>
          </div>
        ))}
        {years.length === 0 && <div className="text-center py-10 text-slate-500">Aucun exercice. Cliquez sur "Nouvel exercice" pour en creer un.</div>}
      </div>
      {showNew && <FiscalYearDialog api={api} societeId={societeId} onClose={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function FiscalYearDialog({ api, societeId, onClose }) {
  const yr = new Date().getFullYear();
  const [f, setF] = useState({ code: `${yr}`, start_date: `${yr}-01-01`, end_date: `${yr}-12-31`, societe_id: societeId });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.post("/accounting/fiscal-years", f); toast.success("Exercice cree"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white">
        <DialogHeader><DialogTitle>Nouvel exercice</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Code (ex: 2026 ou 2025-2026)" value={f.code} onChange={e => setF({ ...f, code: e.target.value })} className={inputCls} />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] text-slate-500">Debut</label><Input type="date" value={f.start_date} onChange={e => setF({ ...f, start_date: e.target.value })} className={inputCls} /></div>
            <div><label className="text-[10px] text-slate-500">Fin</label><Input type="date" value={f.end_date} onChange={e => setF({ ...f, end_date: e.target.value })} className={inputCls} /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] font-bold">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Creer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── TAB: Ecritures ────────────────────────────────
function EntriesTab({ api, societeId }) {
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState({ journal: "", status: "", q: "" });
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { societe_id: societeId, journal: filters.journal || undefined, status: filters.status || undefined, q: filters.q || undefined };
      const r = await api.get("/accounting/entries", { params });
      setEntries(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [societeId, filters.journal, filters.status]);

  const validate = async (id) => {
    try { await api.post(`/accounting/entries/${id}/validate`); toast.success("Ecriture validee"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const extourne = async (id) => {
    if (!window.confirm("Generer une ecriture d'extourne (inverse) ?")) return;
    try { await api.post(`/accounting/entries/${id}/extourne`); toast.success("Extourne creee"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce brouillard ?")) return;
    try { await api.delete(`/accounting/entries/${id}`); toast.success("Supprime"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="entries-tab">
      {/* Cegid sync stats */}
      <CegidSyncStats entries={entries} />

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Input placeholder="Recherche libelle/piece..." value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} onKeyDown={e => e.key === "Enter" && load()} className={`${inputCls} h-9 max-w-xs`} />
        <select value={filters.journal} onChange={e => setFilters({ ...filters, journal: e.target.value })} className={`${inputCls} h-9 px-3 rounded`}>
          <option value="">Tous journaux</option>
          {["AC", "VE", "BQ", "CA", "OD", "AN"].map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })} className={`${inputCls} h-9 px-3 rounded`}>
          <option value="">Tous statuts</option>
          <option value="draft">Brouillard</option>
          <option value="to_review">A verifier</option>
          <option value="validated">Validees</option>
        </select>
        <Button size="sm" onClick={load} variant="outline" className="border-white/10 text-white hover:bg-white/5 h-9 text-xs"><RefreshCw className="w-3.5 h-3.5" /></Button>
        <div className="flex-1" />
        <Button size="sm" onClick={() => setShowNew(true)} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="new-entry-btn">
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Nouvelle ecriture
        </Button>
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-[#f7c935]" /> : (
        <div className="space-y-2">
          {entries.map(e => {
            const sm = STATUS_META[e.status] || STATUS_META.draft;
            return (
            <div key={e.entry_id} className="bg-[#0b0811] border border-white/5 rounded-lg p-3 hover:border-white/10" data-testid={`entry-${e.entry_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-bold" style={{ background: `${sm.color}20`, color: sm.color }}>
                    {e.journal_code}
                  </span>
                  <span className="text-xs text-slate-500">{e.date}</span>
                  {e.piece_ref && <span className="text-[10px] px-1.5 py-0.5 bg-white/5 rounded font-mono">{e.piece_ref}</span>}
                  <span className="text-sm text-white font-medium">{e.label}</span>
                  <span className="text-[9px] uppercase font-bold" style={{ color: sm.color }} data-testid={`entry-status-${e.entry_id}`}>
                    {sm.label}
                  </span>
                  {e.auto && (
                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded flex items-center gap-1" style={{ background: "#38bdf820", color: "#38bdf8" }} title={`Générée automatiquement (${e.source_type})`} data-testid={`auto-badge-${e.entry_id}`}>
                      <Zap className="w-2.5 h-2.5" /> AUTO
                    </span>
                  )}
                  {e.cegid_import_id && (
                    <span
                      className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded flex items-center gap-1"
                      style={{ background: "#10b98120", color: "#10b981" }}
                      title={`Cegid : ${e.cegid_import_id}${e.cegid_synced_at ? " · " + e.cegid_synced_at.slice(0, 10) : ""}`}
                      data-testid={`cegid-badge-${e.entry_id}`}
                    >
                      <Cloud className="w-2.5 h-2.5" /> CEGID
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {e.status !== "validated" && (
                    <>
                      <button onClick={() => setEditEntry(e)} className="text-blue-400 hover:text-blue-300 text-[10px] px-2 py-1 rounded hover:bg-white/5">Modifier</button>
                      <button onClick={() => validate(e.entry_id)} className="text-emerald-400 hover:text-emerald-300 text-[10px] px-2 py-1 rounded hover:bg-white/5"><CheckCircle2 className="w-3.5 h-3.5 inline" /> Valider</button>
                      <button onClick={() => del(e.entry_id)} className="text-red-400 hover:text-red-300 text-[10px] px-2 py-1 rounded hover:bg-white/5"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                  {e.status === "validated" && (
                    <button onClick={() => extourne(e.entry_id)} className="text-orange-400 hover:text-orange-300 text-[10px] px-2 py-1 rounded hover:bg-white/5"><RotateCcw className="w-3.5 h-3.5 inline mr-1" /> Extourner</button>
                  )}
                </div>
              </div>
              <table className="w-full text-[11px]">
                <tbody>
                  {(e.lines || []).map(l => (
                    <tr key={l.line_id} className="border-t border-white/5">
                      <td className="px-2 py-1 font-mono text-slate-300 w-24">{l.account_number}</td>
                      <td className="px-2 py-1 text-slate-400">{l.label || l.account_label}</td>
                      <td className="px-2 py-1 text-slate-500 text-[10px]">{l.partner_label || ""}</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-300 w-24">{l.debit ? l.debit.toFixed(2) : ""}</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-300 w-24">{l.credit ? l.credit.toFixed(2) : ""}</td>
                      <td className="px-2 py-1 text-center w-12">{l.lettrage_code && <span className="text-[10px] px-1 bg-purple-500/15 text-purple-400 rounded font-bold">{l.lettrage_code}</span>}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-white/10 font-bold">
                    <td colSpan="3" className="px-2 py-1 text-slate-500">Totaux</td>
                    <td className="px-2 py-1 text-right font-mono text-[#f7c935]">{e.total_debit?.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right font-mono text-[#f7c935]">{e.total_credit?.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            );
          })}
          {entries.length === 0 && <div className="text-center py-10 text-slate-500">Aucune ecriture. Cliquez sur "Nouvelle ecriture" ou utilisez l'auto-posting depuis Factures/Depenses.</div>}
        </div>
      )}
      {(showNew || editEntry) && <EntryDialog api={api} societeId={societeId} entry={editEntry} onClose={() => { setShowNew(false); setEditEntry(null); load(); }} />}
    </div>
  );
}

function EntryDialog({ api, societeId, entry, onClose }) {
  const [accounts, setAccounts] = useState([]);
  const [years, setYears] = useState([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [f, setF] = useState(entry ? {
    societe_id: societeId,
    journal_code: entry.journal_code,
    fiscal_year_id: entry.fiscal_year_id,
    date: entry.date,
    piece_ref: entry.piece_ref,
    label: entry.label,
    lines: entry.lines.map(l => ({ account_number: l.account_number, label: l.label, debit: l.debit, credit: l.credit, partner_label: l.partner_label || "" })),
  } : {
    societe_id: societeId,
    journal_code: "OD",
    fiscal_year_id: "",
    date: new Date().toISOString().slice(0, 10),
    piece_ref: "",
    label: "",
    lines: [{ account_number: "", label: "", debit: 0, credit: 0, partner_label: "" }, { account_number: "", label: "", debit: 0, credit: 0, partner_label: "" }],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/accounting/accounts", { params: { societe_id: societeId } }).then(r => setAccounts(r.data));
    api.get("/accounting/fiscal-years", { params: { societe_id: societeId } }).then(r => {
      setYears(r.data);
      if (!f.fiscal_year_id && r.data.length > 0) {
        const open = r.data.find(y => y.status === "open");
        setF(p => ({ ...p, fiscal_year_id: open?.fiscal_year_id || r.data[0].fiscal_year_id }));
      }
    });
  // eslint-disable-next-line
  }, [societeId]);

  const totalD = f.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0);
  const totalC = f.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0);
  const balanced = Math.abs(totalD - totalC) < 0.01;

  const setLine = (i, k, v) => {
    const arr = [...f.lines];
    arr[i] = { ...arr[i], [k]: v };
    setF({ ...f, lines: arr });
  };
  const addLine = () => setF({ ...f, lines: [...f.lines, { account_number: "", label: "", debit: 0, credit: 0, partner_label: "" }] });
  const delLine = (i) => setF({ ...f, lines: f.lines.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!balanced) { toast.error("Ecriture non equilibree"); return; }
    setSaving(true);
    try {
      const body = {
        ...f,
        lines: f.lines.map(l => ({ ...l, debit: parseFloat(l.debit) || 0, credit: parseFloat(l.credit) || 0 })).filter(l => l.account_number && (l.debit || l.credit)),
      };
      if (entry) await api.put(`/accounting/entries/${entry.entry_id}`, body);
      else await api.post(`/accounting/entries`, body);
      toast.success(entry ? "Modifiee" : "Creee");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const askAI = async () => {
    if (!f.label) { toast.error("Saisissez d'abord un libelle d'ecriture"); return; }
    const direction = f.journal_code === "VE" ? "vente" : f.journal_code === "AC" ? "achat" : f.journal_code === "BQ" ? (f.lines.some(l => parseFloat(l.debit) > 0 && l.account_number?.startsWith("5")) ? "encaissement" : "paiement") : "achat";
    const amount = Math.max(...f.lines.map(l => parseFloat(l.debit) || parseFloat(l.credit) || 0));
    setAiBusy(true);
    setAiSuggestions([]);
    setF(prev => ({ ...prev, aiAttempted: true }));
    try {
      const r = await api.post("/accounting/ai/suggest-accounts", { societe_id: societeId, label: f.label, amount, direction });
      const s = r.data?.suggestions || [];
      if (!s.length) { toast.warning("L'IA n'a pas trouve de suggestion fiable"); return; }
      setAiSuggestions(s);
      toast.success(`${s.length} suggestion(s) IA`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setAiBusy(false);
  };

  const applySuggestion = (s) => {
    // Find first empty line or replace last
    const idx = f.lines.findIndex(l => !l.account_number);
    if (idx >= 0) setLine(idx, "account_number", s.account_number);
    else { addLine(); setTimeout(() => setLine(f.lines.length, "account_number", s.account_number), 50); }
    toast.success(`Compte ${s.account_number} ajoute`);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-3xl">
        <DialogHeader><DialogTitle>{entry ? "Modifier" : "Nouvelle"} ecriture</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-4 gap-2">
            <select value={f.journal_code} onChange={e => setF({ ...f, journal_code: e.target.value })} className={`${inputCls} h-10 px-3 rounded`}>
              {["AC", "VE", "BQ", "CA", "OD", "AN"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={f.fiscal_year_id} onChange={e => setF({ ...f, fiscal_year_id: e.target.value })} className={`${inputCls} h-10 px-3 rounded`}>
              <option value="">Exercice</option>
              {years.map(y => <option key={y.fiscal_year_id} value={y.fiscal_year_id}>{y.code} ({y.status})</option>)}
            </select>
            <Input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} className={inputCls} />
            <Input placeholder="N° piece" value={f.piece_ref} onChange={e => setF({ ...f, piece_ref: e.target.value })} className={`${inputCls} font-mono`} />
          </div>
          <Input placeholder="Libelle de l'ecriture" value={f.label} onChange={e => setF({ ...f, label: e.target.value })} className={inputCls} data-testid="entry-label-input" />
          {f.label && (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={askAI} disabled={aiBusy} variant="outline" className="border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10 text-xs h-8" data-testid="ai-suggest-btn">
                {aiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Suggerer comptes (IA)
              </Button>
              {aiSuggestions.length > 0 && <span className="text-[10px] text-slate-500">Cliquez une suggestion pour l'appliquer</span>}
              {aiSuggestions.length === 0 && aiBusy === false && f.aiAttempted && <span className="text-[10px] text-slate-500">Aucune suggestion pertinente. Essayez un libelle plus explicite.</span>}
            </div>
          )}
          {aiSuggestions.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="ai-suggestions">
              {aiSuggestions.map((s, i) => (
                <button key={i} onClick={() => applySuggestion(s)} className="text-left bg-[#f7c935]/5 border border-[#f7c935]/20 rounded-lg p-2 hover:bg-[#f7c935]/10 transition-colors" data-testid={`ai-sugg-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-bold text-[#f7c935]">{s.account_number}</span>
                    <span className="text-[10px] text-slate-500">{s.confidence}% conf.</span>
                  </div>
                  <div className="text-xs text-slate-200">{s.label}</div>
                  <div className="text-[10px] text-slate-500 mt-1">{s.reasoning}</div>
                </button>
              ))}
            </div>
          )}
          <div className="border border-white/5 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#0b0811]"><tr>
                <th className="px-2 py-1 text-left">Compte</th>
                <th className="px-2 py-1 text-left">Libelle</th>
                <th className="px-2 py-1 text-left">Tiers</th>
                <th className="px-2 py-1 text-right w-24">Debit</th>
                <th className="px-2 py-1 text-right w-24">Credit</th>
                <th className="w-8"></th>
              </tr></thead>
              <tbody>
                {f.lines.map((l, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="p-1">
                      <input list={`acc-list-${i}`} value={l.account_number} onChange={e => setLine(i, "account_number", e.target.value)}
                        className={`${inputCls} h-8 px-2 font-mono text-xs w-full`} placeholder="411000" data-testid={`line-${i}-account`} />
                      <datalist id={`acc-list-${i}`}>
                        {accounts.map(a => <option key={a.account_id} value={a.account_number}>{a.label}</option>)}
                      </datalist>
                    </td>
                    <td className="p-1"><Input value={l.label} onChange={e => setLine(i, "label", e.target.value)} className={`${inputCls} h-8 text-xs`} /></td>
                    <td className="p-1"><Input value={l.partner_label} onChange={e => setLine(i, "partner_label", e.target.value)} className={`${inputCls} h-8 text-xs`} placeholder="Optionnel" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={l.debit || ""} onChange={e => setLine(i, "debit", e.target.value)} className={`${inputCls} h-8 text-right text-xs`} data-testid={`line-${i}-debit`} /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={l.credit || ""} onChange={e => setLine(i, "credit", e.target.value)} className={`${inputCls} h-8 text-right text-xs`} data-testid={`line-${i}-credit`} /></td>
                    <td className="p-1"><button onClick={() => delLine(i)} className="text-red-400 hover:text-red-300"><X className="w-3.5 h-3.5" /></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-white/10 bg-[#0b0811] font-bold">
                  <td colSpan="3" className="px-2 py-1.5 text-slate-500">
                    <button onClick={addLine} className="text-[#f7c935] hover:underline text-xs"><Plus className="w-3 h-3 inline" /> Ajouter ligne</button>
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono" style={{ color: balanced ? "#22c55e" : "#ef4444" }}>{totalD.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right font-mono" style={{ color: balanced ? "#22c55e" : "#ef4444" }}>{totalC.toFixed(2)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {!balanced && <div className="text-xs text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Difference : {(totalD - totalC).toFixed(2)} EUR</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5">Annuler</Button>
          <Button onClick={save} disabled={saving || !balanced || !f.fiscal_year_id || !f.label} className="bg-[#f7c935] text-[#0b0811] font-bold" data-testid="save-entry-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer en brouillard"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── TAB: Lettrage ─────────────────────────────────
function LettrageTab({ api, societeId }) {
  const [accounts, setAccounts] = useState([]);
  const [accountNumber, setAccountNumber] = useState("411000");
  const [lines, setLines] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/accounting/accounts", { params: { societe_id: societeId } }).then(r => setAccounts((r.data || []).filter(a => a.lettrable)));
  // eslint-disable-next-line
  }, [societeId]);

  const load = async () => {
    if (!accountNumber) return;
    setBusy(true);
    try {
      const r = await api.get(`/accounting/lettrage/account/${accountNumber}`, { params: { societe_id: societeId, unlettered_only: true } });
      setLines(r.data || []);
      setSelected(new Set());
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [societeId, accountNumber]);

  const toggle = (k) => {
    const s = new Set(selected);
    s.has(k) ? s.delete(k) : s.add(k);
    setSelected(s);
  };
  const sumD = useMemo(() => lines.filter(l => selected.has(`${l.entry_id}|${l.line_id}`)).reduce((s, l) => s + l.debit, 0), [lines, selected]);
  const sumC = useMemo(() => lines.filter(l => selected.has(`${l.entry_id}|${l.line_id}`)).reduce((s, l) => s + l.credit, 0), [lines, selected]);

  const lettrageManual = async () => {
    if (!selected.size) return;
    const refs = [...selected].map(k => { const [eid, lid] = k.split("|"); return { entry_id: eid, line_id: lid }; });
    try {
      const r = await api.post("/accounting/lettrage/manual", { societe_id: societeId, account_number: accountNumber, line_refs: refs });
      toast.success(`Lettrage ${r.data.lettrage_code} applique`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const lettrageAuto = async () => {
    try {
      const r = await api.post("/accounting/lettrage/auto", null, { params: { societe_id: societeId, account_number: accountNumber } });
      toast.success(`${r.data.matched_pairs} paire(s) lettree(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="lettrage-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={accountNumber} onChange={e => setAccountNumber(e.target.value)} className={`${inputCls} h-10 px-3 rounded font-mono`}>
          {accounts.map(a => <option key={a.account_number} value={a.account_number}>{a.account_number} - {a.label}</option>)}
        </select>
        <Button size="sm" onClick={lettrageAuto} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="auto-lettrage-btn">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Lettrage auto
        </Button>
        <Button size="sm" onClick={lettrageManual} disabled={!selected.size || Math.abs(sumD - sumC) > 0.01}
          className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="manual-lettrage-btn">
          Lettrer la selection ({selected.size})
        </Button>
        {selected.size > 0 && (
          <span className="text-xs text-slate-400 ml-2">D: {sumD.toFixed(2)} | C: {sumC.toFixed(2)} | <span style={{ color: Math.abs(sumD - sumC) < 0.01 ? "#22c55e" : "#ef4444" }}>{(sumD - sumC).toFixed(2)}</span></span>
        )}
      </div>
      {busy ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-[#f7c935]" /> : (
        <div className="overflow-x-auto rounded-lg border border-white/5">
          <table className="w-full text-xs">
            <thead className="bg-[#0b0811] text-slate-400">
              <tr>
                <th className="w-8"></th>
                <th className="text-left px-2 py-2 font-bold">Date</th>
                <th className="text-left px-2 py-2 font-bold">Piece</th>
                <th className="text-left px-2 py-2 font-bold">Libelle</th>
                <th className="text-left px-2 py-2 font-bold">Tiers</th>
                <th className="text-right px-2 py-2 font-bold">Debit</th>
                <th className="text-right px-2 py-2 font-bold">Credit</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(l => {
                const k = `${l.entry_id}|${l.line_id}`;
                return (
                  <tr key={k} className={`border-t border-white/5 hover:bg-white/[0.03] ${selected.has(k) ? "bg-[#f7c935]/10" : ""}`} data-testid={`lett-row-${k}`}>
                    <td className="px-2 py-2"><input type="checkbox" checked={selected.has(k)} onChange={() => toggle(k)} /></td>
                    <td className="px-2 py-2 text-slate-400">{l.date}</td>
                    <td className="px-2 py-2 font-mono text-slate-400">{l.piece_ref}</td>
                    <td className="px-2 py-2 text-slate-300">{l.label}</td>
                    <td className="px-2 py-2 text-slate-400">{l.partner_label}</td>
                    <td className="px-2 py-2 text-right font-mono">{l.debit ? l.debit.toFixed(2) : ""}</td>
                    <td className="px-2 py-2 text-right font-mono">{l.credit ? l.credit.toFixed(2) : ""}</td>
                  </tr>
                );
              })}
              {lines.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-500">Aucune ligne non-lettree sur ce compte.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────── TAB: Banque ───────────────────────────────────
function BankTab({ api, societeId }) {
  const [imports, setImports] = useState([]);
  const [selected, setSelected] = useState(null);
  const [lines, setLines] = useState([]);
  const [bankAccount, setBankAccount] = useState("512100");
  const [uploading, setUploading] = useState(false);
  const [matching, setMatching] = useState(false);

  const load = async () => {
    try { const r = await api.get("/accounting/bank/imports", { params: { societe_id: societeId } }); setImports(r.data); } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [societeId]);

  const loadLines = async (id) => {
    try { const r = await api.get(`/accounting/bank/imports/${id}/lines`); setLines(r.data); setSelected(id); } catch (e) { toast.error(e.message); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("societe_id", societeId);
      fd.append("bank_account_number", bankAccount);
      const r = await api.post("/accounting/bank/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const a = r.data.auto || {};
      toast.success(`${r.data.lines_imported} ligne(s) importee(s) · ${(a.matched || 0) + (a.validated || 0)} pointee(s) auto · ${a.to_review || 0} a verifier`);
      await load();
      await loadLines(r.data.import_id);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setUploading(false);
    e.target.value = "";
  };

  const matchAuto = async () => {
    if (!selected) return;
    setMatching(true);
    try {
      const r = await api.post("/accounting/bank/match-auto", null, { params: { import_id: selected, societe_id: societeId } });
      toast.success(`${r.data.matched} / ${r.data.total} ligne(s) rapprochee(s)`);
      loadLines(selected);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setMatching(false);
  };

  return (
    <div data-testid="bank-tab">
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-3">
          <BankConnectionsPanel api={api} societeId={societeId} onSynced={load} />
          <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">Importer un releve</h4>
            <Input placeholder="Compte bancaire (ex: 512100)" value={bankAccount} onChange={e => setBankAccount(e.target.value)} className={`${inputCls} font-mono mb-2`} />
            <label className={`block cursor-pointer border-2 border-dashed border-white/10 rounded p-4 text-center hover:border-[#f7c935]/40 transition-colors ${uploading ? "opacity-50" : ""}`}>
              <input type="file" accept=".csv,.ofx,.txt,.pdf" onChange={onUpload} className="hidden" data-testid="bank-import-input" />
              {uploading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
                <>
                  <Upload className="w-5 h-5 text-slate-500 mx-auto mb-1" />
                  <span className="text-xs text-slate-400">CSV / OFX / PDF (lecture IA)</span>
                </>
              )}
            </label>
          </div>
          <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300 mb-2">Imports recents</h4>
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {imports.map(i => (
                <button key={i.import_id} onClick={() => loadLines(i.import_id)}
                  className={`w-full text-left p-2 rounded text-xs ${selected === i.import_id ? "bg-[#f7c935]/15 text-[#f7c935]" : "hover:bg-white/5 text-slate-300"}`}>
                  <div className="font-medium truncate">{i.file_name}</div>
                  <div className="text-[10px] text-slate-500">{i.lines_count} lignes · {i.matched_count} rapprochees · {i.format}</div>
                </button>
              ))}
              {imports.length === 0 && <div className="text-center py-4 text-slate-500 text-xs">Aucun import</div>}
            </div>
          </div>
        </div>
        <div className="lg:col-span-2 bg-[#0b0811] border border-white/5 rounded-lg p-3">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-300">Lignes bancaires</h4>
            <Button size="sm" onClick={matchAuto} disabled={!selected || matching} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-8" data-testid="match-auto-btn">
              {matching ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Rapprocher auto
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-slate-400">
                <tr>
                  <th className="text-left px-2 py-1 font-bold">Date</th>
                  <th className="text-left px-2 py-1 font-bold">Libelle</th>
                  <th className="text-right px-2 py-1 font-bold">Montant</th>
                  <th className="text-center px-2 py-1 font-bold">Statut</th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.bank_line_id} className="border-t border-white/5" data-testid={`bank-line-${l.bank_line_id}`}>
                    <td className="px-2 py-1 text-slate-400">{l.date}</td>
                    <td className="px-2 py-1 text-slate-300 truncate max-w-md">{l.label}</td>
                    <td className="px-2 py-1 text-right font-mono" style={{ color: l.amount > 0 ? "#22c55e" : "#ef4444" }}>{l.amount.toFixed(2)}</td>
                    <td className="px-2 py-1 text-center">
                      {l.status === "matched" ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 font-bold">RAPPROCHE</span>
                        : l.status === "to_review" ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 font-bold">A VERIFIER</span>
                        : l.status === "ignored" ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-500 font-bold">IGNOREE</span>
                        : <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-500/15 text-slate-400 font-bold">EN ATTENTE</span>}
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && <tr><td colSpan="4" className="text-center py-10 text-slate-500">Selectionnez un import pour voir ses lignes</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────── TAB: Reports ──────────────────────────────────
function ReportsTab({ api, societeId }) {
  const [years, setYears] = useState([]);
  const [fyId, setFyId] = useState("");
  const [reportType, setReportType] = useState("balance");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [grandLivreAccount, setGrandLivreAccount] = useState("");
  const [tvaPeriod, setTvaPeriod] = useState(`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}`);
  const [showMonthlyConfig, setShowMonthlyConfig] = useState(false);

  useEffect(() => {
    api.get("/accounting/fiscal-years", { params: { societe_id: societeId } }).then(r => {
      setYears(r.data);
      if (r.data.length) setFyId(r.data[0].fiscal_year_id);
    });
  // eslint-disable-next-line
  }, [societeId]);

  const load = async () => {
    if (!fyId) return;
    setLoading(true);
    setData(null);
    try {
      const params = { societe_id: societeId, fiscal_year_id: fyId };
      if (reportType === "grand-livre" && grandLivreAccount) params.account_number = grandLivreAccount;
      if (reportType === "tva-ca3") params.period = tvaPeriod;
      const r = await api.get(`/accounting/reports/${reportType}`, { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fyId, reportType, tvaPeriod]);

  const downloadFec = async () => {
    if (!fyId) return;
    try {
      const r = await api.get(`/accounting/reports/fec`, { params: { societe_id: societeId, fiscal_year_id: fyId }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `FEC_${fyId}.txt`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const downloadDossierZip = async () => {
    if (!fyId) return;
    try {
      toast.info("Generation du dossier comptable...");
      const r = await api.get(`/accounting/reports/dossier-zip`, { params: { societe_id: societeId, fiscal_year_id: fyId }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `dossier_comptable_${fyId}.zip`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Dossier comptable telecharge");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="reports-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select value={fyId} onChange={e => setFyId(e.target.value)} className={`${inputCls} h-9 px-3 rounded`}>
          <option value="">Choisir un exercice</option>
          {years.map(y => <option key={y.fiscal_year_id} value={y.fiscal_year_id}>{y.code}</option>)}
        </select>
        {[
          { v: "balance", l: "Balance" },
          { v: "grand-livre", l: "Grand-livre" },
          { v: "bilan", l: "Bilan" },
          { v: "compte-resultat", l: "Compte de resultat" },
          { v: "sig", l: "SIG" },
          { v: "tva-ca3", l: "TVA CA3" },
          { v: "liasse", l: "Liasse fiscale" },
        ].map(t => (
          <button key={t.v} onClick={() => setReportType(t.v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold ${reportType === t.v ? "bg-[#f7c935] text-[#0b0811]" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
            data-testid={`report-${t.v}-btn`}>
            {t.l}
          </button>
        ))}
        {reportType === "grand-livre" && (
          <Input placeholder="Filtrer par compte (optionnel)" value={grandLivreAccount} onChange={e => setGrandLivreAccount(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} className={`${inputCls} h-9 max-w-xs`} />
        )}
        {reportType === "tva-ca3" && (
          <Input type="month" value={tvaPeriod} onChange={e => setTvaPeriod(e.target.value)} className={`${inputCls} h-9 max-w-[180px]`} data-testid="tva-period" />
        )}
        <div className="flex-1" />
        <Button size="sm" onClick={() => setShowMonthlyConfig(true)} variant="outline" className="border-[#f7c935]/30 text-[#f7c935] hover:bg-[#f7c935]/10 text-xs h-9" data-testid="monthly-report-btn">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Rapport mensuel auto
        </Button>
        <Button size="sm" onClick={downloadFec} disabled={!fyId} className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 font-bold text-xs h-9" data-testid="export-fec-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export FEC
        </Button>
        <Button size="sm" onClick={downloadDossierZip} disabled={!fyId} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold text-xs h-9" data-testid="export-dossier-zip-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Dossier ZIP complet
        </Button>
      </div>
      {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto my-10 text-[#f7c935]" /> : data && (
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4">
          {reportType === "balance" && <BalanceView data={data} />}
          {reportType === "grand-livre" && <GrandLivreView data={data} />}
          {reportType === "bilan" && <BilanView data={data} />}
          {reportType === "compte-resultat" && <CRView data={data} />}
          {reportType === "sig" && <SigView data={data} />}
          {reportType === "tva-ca3" && <TvaCa3View data={data} />}
          {reportType === "liasse" && <LiasseView data={data} />}
        </div>
      )}
      {showMonthlyConfig && <MonthlyReportDialog api={api} societeId={societeId} onClose={() => setShowMonthlyConfig(false)} />}
    </div>
  );
}

function MonthlyReportDialog({ api, societeId, onClose }) {
  const [cfg, setCfg] = useState({ enabled: false, recipients: [], day_of_month: 5 });
  const [recipientInput, setRecipientInput] = useState("");
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    const m = now.getMonth() === 0 ? 12 : now.getMonth();
    const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    return `${y}-${String(m).padStart(2, "0")}`;
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/accounting/settings", { params: { societe_id: societeId } }).then(r => {
      const s = r.data || {};
      setCfg({
        enabled: !!s.monthly_report_enabled,
        recipients: s.monthly_report_recipients || [],
        day_of_month: s.monthly_report_day || 5,
      });
    });
  // eslint-disable-next-line
  }, [societeId]);

  const addRecipient = () => {
    const e = recipientInput.trim();
    if (!e || !/.+@.+\..+/.test(e)) { toast.error("Email invalide"); return; }
    if (cfg.recipients.includes(e)) return;
    setCfg(c => ({ ...c, recipients: [...c.recipients, e] }));
    setRecipientInput("");
  };
  const removeRecipient = (e) => setCfg(c => ({ ...c, recipients: c.recipients.filter(x => x !== e) }));

  const saveCfg = async () => {
    setBusy(true);
    try {
      await api.put("/accounting/settings/monthly-report", { ...cfg, societe_id: societeId });
      toast.success("Configuration sauvegardee");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const previewReport = async () => {
    setBusy(true);
    setPreview(null);
    try {
      const r = await api.get("/accounting/reports/monthly-email/preview", { params: { societe_id: societeId, period } });
      setPreview(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };

    const sendNow = async () => {
    if (!cfg.recipients.length) { toast.error("Ajoutez au moins un destinataire"); return; }
    setSending(true);
    try {
      const r = await api.post(`/accounting/reports/monthly-email/send`, null, {
        params: { societe_id: societeId, period, recipients: cfg.recipients.join(",") },
      });
      toast.success(`Email envoye a ${r.data.sent} destinataire(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSending(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-3xl">
        <DialogHeader><DialogTitle>Rapport mensuel automatique</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-xs text-slate-400">Recevez chaque mois un email avec les SIG, la TVA a decaisser et les alertes critiques (EBE negatif, resultat net negatif, TVA elevee).</p>

          <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="monthly-enabled" />
              <span className="text-sm font-bold">Activer l'envoi mensuel automatique</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 uppercase tracking-wider">Jour d'envoi (1-28)</label>
                <Input type="number" min="1" max="28" value={cfg.day_of_month} onChange={e => setCfg({ ...cfg, day_of_month: parseInt(e.target.value) || 5 })} className={inputCls} data-testid="monthly-day" />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider">Destinataires</label>
              <div className="flex gap-2 mt-1">
                <Input placeholder="dirigeant@societe.fr" value={recipientInput} onChange={e => setRecipientInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addRecipient())} className={inputCls} data-testid="monthly-recipient-input" />
                <Button size="sm" onClick={addRecipient} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-10" data-testid="monthly-add-recipient">Ajouter</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {cfg.recipients.map(r => (
                  <span key={r} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#f7c935]/10 text-[#f7c935] text-xs" data-testid={`recipient-${r}`}>
                    {r}
                    <button onClick={() => removeRecipient(r)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            </div>
            <Button size="sm" onClick={saveCfg} disabled={busy} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-9 w-full" data-testid="monthly-save-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />} Enregistrer
            </Button>
          </div>

          <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Tester / Envoyer maintenant</h4>
            <div className="flex items-center gap-2 mb-2">
              <Input type="month" value={period} onChange={e => setPeriod(e.target.value)} className={`${inputCls} max-w-[180px] h-9`} data-testid="monthly-period" />
              <Button size="sm" onClick={previewReport} disabled={busy} variant="outline" className="border-white/10 text-white hover:bg-white/5 text-xs h-9" data-testid="monthly-preview-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Apercu"}
              </Button>
              <Button size="sm" onClick={sendNow} disabled={sending || !cfg.recipients.length} className="bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 font-bold text-xs h-9" data-testid="monthly-send-now-btn">
                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />} Envoyer maintenant
              </Button>
            </div>
            {preview && (
              <div className="mt-2 max-h-[300px] overflow-y-auto bg-white rounded-lg" data-testid="monthly-preview">
                <div className="p-2" dangerouslySetInnerHTML={{ __html: preview.html }} />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white hover:bg-white/5">Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BalanceView({ data }) {
  return (
    <div data-testid="balance-view">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">Balance</h3>
        <span className="text-xs text-slate-500">Total D/C : {data.total_debit?.toFixed(2)} / {data.total_credit?.toFixed(2)} EUR</span>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-400 border-b border-white/10">
          <tr>
            <th className="text-left px-2 py-2 font-bold">Compte</th>
            <th className="text-left px-2 py-2 font-bold">Libelle</th>
            <th className="text-right px-2 py-2 font-bold">Mouv. Debit</th>
            <th className="text-right px-2 py-2 font-bold">Mouv. Credit</th>
            <th className="text-right px-2 py-2 font-bold">Solde</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map(r => (
            <tr key={r.account_number} className="border-t border-white/5">
              <td className="px-2 py-1.5 font-mono text-white">{r.account_number}</td>
              <td className="px-2 py-1.5 text-slate-300">{r.label}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-400">{r.debit?.toFixed(2) || "-"}</td>
              <td className="px-2 py-1.5 text-right font-mono text-slate-400">{r.credit?.toFixed(2) || "-"}</td>
              <td className="px-2 py-1.5 text-right font-mono font-bold" style={{ color: r.balance >= 0 ? "#22c55e" : "#ef4444" }}>{r.balance?.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GrandLivreView({ data }) {
  let cumul = 0;
  return (
    <div data-testid="grand-livre-view">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">Grand-livre</h3>
        <span className="text-xs text-slate-500">{data.count} ligne(s)</span>
      </div>
      <table className="w-full text-xs">
        <thead className="text-slate-400 border-b border-white/10">
          <tr>
            <th className="text-left px-2 py-2 font-bold">Date</th>
            <th className="text-left px-2 py-2 font-bold">Jou.</th>
            <th className="text-left px-2 py-2 font-bold">Compte</th>
            <th className="text-left px-2 py-2 font-bold">Libelle</th>
            <th className="text-right px-2 py-2 font-bold">Debit</th>
            <th className="text-right px-2 py-2 font-bold">Credit</th>
            <th className="text-right px-2 py-2 font-bold">Cumul</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r, i) => {
            cumul += (r.debit || 0) - (r.credit || 0);
            return (
              <tr key={i} className="border-t border-white/5">
                <td className="px-2 py-1.5 text-slate-400">{r.date}</td>
                <td className="px-2 py-1.5 font-mono text-slate-400">{r.journal_code}</td>
                <td className="px-2 py-1.5 font-mono text-white">{r.account_number}</td>
                <td className="px-2 py-1.5 text-slate-300">{r.label}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.debit?.toFixed(2) || ""}</td>
                <td className="px-2 py-1.5 text-right font-mono">{r.credit?.toFixed(2) || ""}</td>
                <td className="px-2 py-1.5 text-right font-mono text-[#f7c935]">{cumul.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function BilanView({ data }) {
  return (
    <div data-testid="bilan-view">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">Bilan {data.fiscal_year?.code}</h3>
        <span className="text-xs" style={{ color: data.balanced ? "#22c55e" : "#ef4444" }}>
          {data.balanced ? "✓ Equilibre" : "✗ Non equilibre"}
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-white/10">ACTIF</h4>
          <table className="w-full text-xs">
            <tbody>
              {data.actif.map(r => (
                <tr key={r.account_number} className="border-b border-white/5">
                  <td className="py-1.5 font-mono text-slate-400 w-20">{r.account_number}</td>
                  <td className="py-1.5 text-slate-300">{r.label}</td>
                  <td className="py-1.5 text-right font-mono">{r.amount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="font-bold"><td colSpan="2" className="py-2">Total Actif</td><td className="py-2 text-right font-mono text-[#f7c935]">{data.total_actif.toFixed(2)}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-white/10">PASSIF</h4>
          <table className="w-full text-xs">
            <tbody>
              {data.passif.map(r => (
                <tr key={r.account_number} className="border-b border-white/5">
                  <td className="py-1.5 font-mono text-slate-400 w-20">{r.account_number}</td>
                  <td className="py-1.5 text-slate-300">{r.label}</td>
                  <td className="py-1.5 text-right font-mono">{r.amount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="font-bold"><td colSpan="2" className="py-2">Total Passif</td><td className="py-2 text-right font-mono text-[#f7c935]">{data.total_passif.toFixed(2)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 text-center text-sm" style={{ color: data.result >= 0 ? "#22c55e" : "#ef4444" }}>
        Resultat de l'exercice : <strong>{data.result?.toFixed(2)} EUR</strong>
      </div>
    </div>
  );
}

function CRView({ data }) {
  return (
    <div data-testid="cr-view">
      <h3 className="text-sm font-bold text-white mb-3">Compte de resultat</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-white/10">CHARGES</h4>
          <table className="w-full text-xs">
            <tbody>
              {data.charges.map(r => (
                <tr key={r.account_number} className="border-b border-white/5">
                  <td className="py-1.5 font-mono text-slate-400 w-20">{r.account_number}</td>
                  <td className="py-1.5 text-slate-300">{r.label}</td>
                  <td className="py-1.5 text-right font-mono">{r.amount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="font-bold"><td colSpan="2" className="py-2">Total Charges</td><td className="py-2 text-right font-mono text-red-400">{data.total_charges.toFixed(2)}</td></tr>
            </tbody>
          </table>
        </div>
        <div>
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 pb-1 border-b border-white/10">PRODUITS</h4>
          <table className="w-full text-xs">
            <tbody>
              {data.produits.map(r => (
                <tr key={r.account_number} className="border-b border-white/5">
                  <td className="py-1.5 font-mono text-slate-400 w-20">{r.account_number}</td>
                  <td className="py-1.5 text-slate-300">{r.label}</td>
                  <td className="py-1.5 text-right font-mono">{r.amount.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="font-bold"><td colSpan="2" className="py-2">Total Produits</td><td className="py-2 text-right font-mono text-emerald-400">{data.total_produits.toFixed(2)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-4 text-center text-base" style={{ color: data.result >= 0 ? "#22c55e" : "#ef4444" }}>
        Resultat : <strong className="text-2xl">{data.result?.toFixed(2)} EUR</strong>
      </div>
    </div>
  );
}

// ─────────── REPORT VIEWS: SIG / TVA CA3 / Liasse ─────────
function SigView({ data }) {
  const lines = [
    { k: "marge_commerciale", l: "Marge commerciale", h: "707 - 607" },
    { k: "production_exercice", l: "Production de l'exercice", h: "70 (hors 707)" },
    { k: "valeur_ajoutee", l: "Valeur ajoutee (VA)", h: "Marge + Prod - Conso tiers" },
    { k: "ebe", l: "Excedent Brut d'Exploitation (EBE)", h: "VA + Subv - Impots - Personnel", emphasis: true },
    { k: "resultat_exploitation", l: "Resultat d'exploitation", h: "EBE +/- DAP, autres" },
    { k: "resultat_financier", l: "Resultat financier", h: "76 - 66" },
    { k: "resultat_courant", l: "Resultat courant avant impots", h: "Expl + Fin", emphasis: true },
    { k: "resultat_exceptionnel", l: "Resultat exceptionnel", h: "77 - 67" },
    { k: "resultat_net", l: "Resultat net comptable", h: "Courant + Except - IS", emphasis: true },
  ];
  return (
    <div data-testid="sig-view">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><Calculator className="w-4 h-4 text-[#f7c935]" /> Soldes Intermediaires de Gestion</h3>
      <table className="w-full text-xs">
        <thead className="text-slate-400 border-b border-white/10">
          <tr>
            <th className="text-left px-2 py-2 font-bold">Indicateur</th>
            <th className="text-left px-2 py-2 font-bold text-[10px]">Formule</th>
            <th className="text-right px-2 py-2 font-bold">Montant (EUR)</th>
          </tr>
        </thead>
        <tbody>
          {lines.map(l => (
            <tr key={l.k} className={`border-t border-white/5 ${l.emphasis ? "bg-[#f7c935]/5" : ""}`}>
              <td className={`px-2 py-2 ${l.emphasis ? "font-bold text-white" : "text-slate-300"}`}>{l.l}</td>
              <td className="px-2 py-2 text-[10px] text-slate-500 font-mono">{l.h}</td>
              <td className={`px-2 py-2 text-right font-mono ${l.emphasis ? "font-black text-[#f7c935]" : ""}`} style={{ color: !l.emphasis ? (data[l.k] >= 0 ? "#22c55e" : "#ef4444") : undefined }}>
                {data[l.k]?.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <details className="mt-4 text-xs">
        <summary className="cursor-pointer text-slate-500 hover:text-white">Detail des composantes</summary>
        <div className="grid sm:grid-cols-2 gap-2 mt-2">
          {Object.entries(data.details || {}).map(([k, v]) => (
            <div key={k} className="bg-[#0b0811] border border-white/5 rounded p-2 flex justify-between">
              <span className="text-slate-500">{k}</span>
              <span className="font-mono text-slate-300">{v?.toFixed(2)}</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function TvaCa3View({ data }) {
  return (
    <div data-testid="tva-ca3-view">
      <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><FileText className="w-4 h-4 text-[#f7c935]" /> Declaration TVA CA3 — {data.period}</h3>
      <p className="text-xs text-slate-500 mb-4">Periode du {data.date_from} au {data.date_to}</p>
      <div className="bg-[#0b0811] border border-white/5 rounded-lg overflow-hidden mb-4">
        <table className="w-full text-xs">
          <thead className="bg-[#14101c] text-slate-400">
            <tr>
              <th className="text-left px-2 py-2 font-bold">Ligne</th>
              <th className="text-left px-2 py-2 font-bold">Designation</th>
              <th className="text-right px-2 py-2 font-bold">Base HT</th>
              <th className="text-right px-2 py-2 font-bold">TVA collectee</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(data.ca3_lines || {}).map(([code, line]) => (
              <tr key={code} className="border-t border-white/5">
                <td className="px-2 py-1.5 font-mono text-[#f7c935] font-bold">{code.split("_")[0]}</td>
                <td className="px-2 py-1.5 text-slate-300">{line.label}</td>
                <td className="px-2 py-1.5 text-right font-mono">{line.base?.toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right font-mono">{line.tva?.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Total CA HT</div>
          <div className="text-lg font-black text-white">{data.totals?.ca_ht?.toFixed(2)} EUR</div>
        </div>
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">TVA collectee</div>
          <div className="text-lg font-black text-emerald-400">{data.totals?.tva_collectee?.toFixed(2)} EUR</div>
        </div>
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3">
          <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">TVA deductible (dont immo)</div>
          <div className="text-lg font-black text-blue-400">{data.totals?.tva_deductible_total?.toFixed(2)} EUR</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Normale {data.totals?.tva_deductible_normal?.toFixed(2)} | Immo {data.totals?.tva_deductible_immo?.toFixed(2)}</div>
        </div>
        <div className="bg-[#f7c935]/10 border border-[#f7c935]/30 rounded-lg p-3">
          <div className="text-[10px] text-[#f7c935] uppercase tracking-wider mb-1 font-bold">{data.totals?.tva_a_decaisser > 0 ? "TVA a decaisser" : "Credit a reporter"}</div>
          <div className="text-2xl font-black text-[#f7c935]">{(data.totals?.tva_a_decaisser || data.totals?.credit_a_reporter)?.toFixed(2)} EUR</div>
        </div>
      </div>
    </div>
  );
}

function LiasseView({ data }) {
  const renderForm = (formKey, title, items, totalKeys = []) => {
    const form = data[formKey] || {};
    const labels = {
      // 2050 actif
      immo_incorporelles_brut: "Immobilisations incorporelles (brut)",
      immo_corporelles_brut: "Immobilisations corporelles (brut)",
      amortissements: "Amortissements et depreciations",
      stocks: "Stocks et en-cours",
      creances_clients: "Creances clients",
      autres_creances: "Autres creances",
      disponibilites: "Disponibilites (banque, caisse)",
      charges_constatees_avance: "Charges constatees d'avance",
      total_actif_brut: "TOTAL ACTIF BRUT",
      total_actif_net: "TOTAL ACTIF NET",
      // 2051 passif
      capitaux_propres: "Capitaux propres (capital + reserves + report)",
      resultat_exercice: "Resultat de l'exercice",
      emprunts: "Emprunts et dettes financieres",
      dettes_fournisseurs: "Dettes fournisseurs",
      dettes_fiscales_sociales: "Dettes fiscales et sociales",
      autres_dettes: "Autres dettes",
      produits_constates_avance: "Produits constates d'avance",
      total_passif: "TOTAL PASSIF",
      // 2052 cr
      ca_ht: "Chiffre d'affaires net (CA HT)",
      production_stockee: "Production stockee",
      production_immobilisee: "Production immobilisee",
      subventions_exploitation: "Subventions d'exploitation",
      achats_marchandises: "Achats marchandises",
      achats_matieres: "Achats matieres premieres",
      autres_achats: "Autres achats et charges externes",
      services_exterieurs: "Services exterieurs",
      impots_taxes: "Impots, taxes",
      charges_personnel: "Charges de personnel",
      dotations_amortissements: "Dotations aux amortissements",
      produits_financiers: "Produits financiers",
      charges_financieres: "Charges financieres",
      produits_exceptionnels: "Produits exceptionnels",
      charges_exceptionnelles: "Charges exceptionnelles",
      impots_societes: "Impot sur les societes",
      total_produits: "TOTAL PRODUITS",
      total_charges: "TOTAL CHARGES",
      resultat: "RESULTAT",
    };
    return (
      <div className="bg-[#0b0811] border border-white/5 rounded-lg overflow-hidden">
        <div className="px-3 py-2 bg-[#14101c] text-xs font-bold uppercase tracking-wider text-slate-300 border-b border-white/10">{title}</div>
        <table className="w-full text-xs">
          <tbody>
            {items.map(k => {
              const isTotal = totalKeys.includes(k);
              return (
                <tr key={k} className={`border-t border-white/5 ${isTotal ? "bg-[#f7c935]/5 font-bold" : ""}`}>
                  <td className="px-3 py-1.5 text-slate-300">{labels[k] || k}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${isTotal ? "text-[#f7c935] font-black" : "text-slate-200"}`}>
                    {(form[k] ?? 0).toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };
  return (
    <div data-testid="liasse-view">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-white flex items-center gap-2"><FileBarChart className="w-4 h-4 text-[#f7c935]" /> Liasse fiscale (CERFA 2050-2052) — synthese</h3>
        <span className="text-[10px]" style={{ color: data.balanced ? "#22c55e" : "#ef4444" }}>
          {data.balanced ? "✓ Bilan equilibre" : "✗ Bilan non equilibre"}
        </span>
      </div>
      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        {renderForm("form_2050_actif", "2050 — Bilan Actif", [
          "immo_incorporelles_brut", "immo_corporelles_brut", "amortissements",
          "stocks", "creances_clients", "autres_creances", "disponibilites",
          "charges_constatees_avance", "total_actif_brut", "total_actif_net",
        ], ["total_actif_brut", "total_actif_net"])}
        {renderForm("form_2051_passif", "2051 — Bilan Passif", [
          "capitaux_propres", "resultat_exercice", "emprunts",
          "dettes_fournisseurs", "dettes_fiscales_sociales", "autres_dettes",
          "produits_constates_avance", "total_passif",
        ], ["total_passif"])}
      </div>
      {renderForm("form_2052_cr", "2052 — Compte de resultat", [
        "ca_ht", "production_stockee", "production_immobilisee", "subventions_exploitation",
        "achats_marchandises", "achats_matieres", "autres_achats", "services_exterieurs",
        "impots_taxes", "charges_personnel", "dotations_amortissements",
        "produits_financiers", "charges_financieres",
        "produits_exceptionnels", "charges_exceptionnelles",
        "impots_societes",
        "total_produits", "total_charges", "resultat",
      ], ["total_produits", "total_charges", "resultat"])}
      <p className="text-[10px] text-slate-500 mt-3">Note : ces chiffres sont indicatifs et destines a etre transposes sur les formulaires CERFA officiels. Pour la liasse complete (2050-2059) avec annexes, utilisez votre expert-comptable ou un logiciel de teletransmission EDI-TDFC.</p>
    </div>
  );
}

// ─────────── TAB: Consolidation groupe ─────────────────────
function ConsolidatedTab({ api, societes }) {
  const [selected, setSelected] = useState(new Set());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(`${new Date().getFullYear()}`);

  const toggle = (sid) => {
    const s = new Set(selected);
    s.has(sid) ? s.delete(sid) : s.add(sid);
    setSelected(s);
  };

  const load = async () => {
    if (!selected.size) { toast.error("Selectionnez au moins une societe"); return; }
    setLoading(true);
    try {
      const r = await api.get("/accounting/reports/consolidated/balance", { params: { societe_ids: [...selected].join(","), fiscal_year_code: year || undefined } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setLoading(false);
  };

  return (
    <div data-testid="consolidated-tab">
      <div className="mb-4 bg-[#0b0811] border border-white/5 rounded-lg p-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Selection des societes a consolider</h3>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
          {(societes || []).map(s => (
            <label key={s.societe_id} className={`flex items-center gap-2 p-2 rounded cursor-pointer ${selected.has(s.societe_id) ? "bg-[#f7c935]/15 text-[#f7c935]" : "hover:bg-white/5 text-slate-300"}`}>
              <input type="checkbox" checked={selected.has(s.societe_id)} onChange={() => toggle(s.societe_id)} />
              <span className="text-xs">{s.name}</span>
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-3">
          <Input placeholder="Code exercice (ex: 2025)" value={year} onChange={e => setYear(e.target.value)} className={`${inputCls} h-9 max-w-xs`} />
          <Button size="sm" onClick={load} disabled={!selected.size || loading} className="bg-[#f7c935] text-[#0b0811] font-bold text-xs h-9" data-testid="consolidate-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Calculator className="w-3.5 h-3.5 mr-1.5" />} Consolider
          </Button>
        </div>
      </div>
      {data && (
        <div className="bg-[#0b0811] border border-white/5 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white">Balance consolidee ({data.societe_count} societes)</h3>
            <span className="text-xs text-slate-500">D/C : {data.total_debit.toFixed(2)} / {data.total_credit.toFixed(2)} EUR</span>
          </div>
          <table className="w-full text-xs">
            <thead className="text-slate-400 border-b border-white/10">
              <tr>
                <th className="text-left px-2 py-2 font-bold">Compte</th>
                <th className="text-left px-2 py-2 font-bold">Libelle</th>
                <th className="text-right px-2 py-2 font-bold">Debit</th>
                <th className="text-right px-2 py-2 font-bold">Credit</th>
                <th className="text-right px-2 py-2 font-bold">Solde</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.account_number} className="border-t border-white/5">
                  <td className="px-2 py-1.5 font-mono text-white">{r.account_number}</td>
                  <td className="px-2 py-1.5 text-slate-300">{r.label}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.debit.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.credit.toFixed(2)}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold" style={{ color: r.balance >= 0 ? "#22c55e" : "#ef4444" }}>{r.balance.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────── CEGID SYNC STATS BANNER ────────────────────────
function CegidSyncStats({ entries }) {
  if (!entries?.length) return null;
  const currentMonth = new Date().toISOString().slice(0, 7);
  const synced = entries.filter(e => e.cegid_import_id);
  const syncedThisMonth = synced.filter(e => (e.cegid_synced_at || "").slice(0, 7) === currentMonth);
  const total = entries.length;
  const pending = entries.filter(e => !e.cegid_import_id && e.status !== "draft").length;
  const pct = total > 0 ? Math.round((synced.length / total) * 100) : 0;

  return (
    <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="cegid-sync-stats">
      <div className="bg-[#10b981]/10 border border-[#10b981]/20 rounded-lg p-3" data-testid="cegid-stat-month">
        <div className="text-[9px] uppercase tracking-wider text-[#10b981] font-bold mb-0.5 flex items-center gap-1">
          <Cloud className="w-3 h-3" /> Synchronisees ce mois
        </div>
        <div className="text-2xl font-black text-[#10b981]">{syncedThisMonth.length}</div>
      </div>
      <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid="cegid-stat-total">
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Total Cegid</div>
        <div className="text-2xl font-black text-white">{synced.length} <span className="text-[11px] text-slate-500 font-normal">/ {total}</span></div>
      </div>
      <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid="cegid-stat-pct">
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Couverture</div>
        <div className="text-2xl font-black" style={{ color: pct >= 80 ? "#22c55e" : pct >= 50 ? "#f7c935" : "#ef4444" }}>{pct}%</div>
      </div>
      <div className="bg-[#0b0811] border border-white/5 rounded-lg p-3" data-testid="cegid-stat-pending">
        <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">A synchroniser</div>
        <div className="text-2xl font-black" style={{ color: pending > 0 ? "#f7c935" : "#94a3b8" }}>{pending}</div>
      </div>
    </div>
  );
}

